import { ICONS, IMAGES } from '@/constants/constants';
import Image from 'next/image';
import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

interface VehicleSizeProps {
    onSizeSelect: (size: { height: string; length: string }) => void;
    onClose: () => void;
    triggerElement: HTMLElement | null;
}


const standardHeight = [
    {
        height: 'Standard',
        image: ICONS.sh1,
    },
    // {
    //     height: 'Medium',
    //     image: ICONS.sh2,
    // },
  
    {
        height: 'Very Tall',
        image: ICONS.sh4,
    },
]
const standardLength = [
  
    {
        length: 'Medium',
        image: ICONS.sl2,
    },
    {
        length: 'Tall',
        image: ICONS.sl3,
    },
    {
        length: 'Very Tall',
        image: ICONS.sl4,
    },
]
const fullSizeHeight = [
    {
        height: 'Standard',
        image: ICONS.fs1,
    },
    {
        height: 'Medium',
        image: ICONS.fs2,
    },
    {
        height: 'Tall',
        image: ICONS.fs3,
    },
    {
        height: 'Very Tall',
        image: ICONS.fs4,
    },
]
const fullSizeLength = [
    {
        length: 'Standard',
        image: ICONS.fl1,
    },
    {
        length: 'Medium',
        image: ICONS.fl2,
    },
    {
        length: 'Tall',
        image: ICONS.fl3,
    },
]

const VehicleSize = ({ onSizeSelect, onClose, triggerElement }: VehicleSizeProps) => {
    const [position, setPosition] = useState({ top: 0, left: 0 })
    const [isPositioned, setIsPositioned] = useState(false)
    const [selectedHeight, setSelectedHeight] = useState('Standard')
    const [selectedLength, setSelectedLength] = useState('Standard')
    const selectorRef = useRef<HTMLDivElement>(null)

    // Calculate position based on trigger element
    useEffect(() => {
        if (triggerElement) {
            requestAnimationFrame(() => {
                const rect = triggerElement.getBoundingClientRect()
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop
                const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
                const viewportWidth = window.innerWidth
                const viewportHeight = window.innerHeight
                
                // Estimate dropdown width based on screen size
                let dropdownWidth = 600 // default for xl screens
                if (viewportWidth < 640) dropdownWidth = viewportWidth * 0.95 // sm: 95vw
                else if (viewportWidth < 768) dropdownWidth = viewportWidth * 0.9 // md: 90vw
                else if (viewportWidth < 1024) dropdownWidth = viewportWidth * 0.8 // lg: 80vw
                else if (viewportWidth < 1280) dropdownWidth = viewportWidth * 0.6 // xl: 60vw
                else dropdownWidth = Math.min(viewportWidth * 0.5, 600) // xl: 50vw, max 600px
                
                let left = rect.left + scrollLeft
                let top = rect.bottom + scrollTop + 4
                
                // Adjust horizontal position if dropdown would overflow
                if (left + dropdownWidth > viewportWidth) {
                    left = Math.max(8, viewportWidth - dropdownWidth - 8) // 8px margin from edge
                }
                
                // Adjust vertical position if dropdown would overflow
                const estimatedHeight = 600 // estimated dropdown height
                const spaceBelow = viewportHeight - (rect.bottom - scrollTop)
                const spaceAbove = rect.top - scrollTop
                
                // Only move above if there's insufficient space below AND enough space above
                if (spaceBelow < estimatedHeight && spaceAbove > estimatedHeight) {
                    top = rect.top + scrollTop - estimatedHeight - 4 // Show above trigger
                }
                // Otherwise keep it below (default behavior)

                setPosition({ top, left })
                setIsPositioned(true)
            })
        }
    }, [triggerElement])

    // Close selector when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onClose])

    const handleSubmit = () => {
        onSizeSelect({ height: selectedHeight, length: selectedLength })
        onClose()
    }

    const selectorContent = isPositioned ? (
        <div
            ref={selectorRef}
            className="absolute bg-white border border-gray-300 rounded-lg shadow-xl z-[9999] w-[95vw] sm:w-[90vw] md:w-[80vw] lg:w-[60vw] xl:w-[50vw] max-w-[600px]"
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`
            }}
        >
            {/* Header */}
            <div className="bg-red-700 text-white p-4 rounded-t-lg">
                <h2 className="text-lg font-bold font-rakesly">VEHICLE SIZE (INCLUDING ROOF LUGGAGE)</h2>
            </div>

            <div className="p-6">
                {/* Height Section */}
                <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Height</h3>

                    <div className="grid grid-cols-2 gap-6">
                        {/* Standard Height */}
                        <div className="space-y-3">
                            <div className="flex items-center">
                                <input
                                    type="radio"
                                    id="height-standard"
                                    name="height"
                                    value="Standard"
                                    checked={selectedHeight === 'Standard'}
                                    onChange={(e) => setSelectedHeight(e.target.value)}
                                    className="w-4 h-4 text-red-500 border-gray-300 focus:ring-red-500 focus:ring-2 accent-red-500"
                                />
                                <label htmlFor="height-standard" className="ml-2 text-lg font-medium text-gray-900">
                                    Standard
                                </label>
                            </div>
                            <p className="text-gray-600 text-sm">
                                Standard: &lt;6m length; &lt;1.83m height.
                            </p>
                          <div className='flex gap-3'>
                              {
                                standardHeight.map((item, index) => (
                                    <div key={index} className='flex items-center '>
                                        <Image className='w-12 h-12' src={item.image} height={20} width={20} alt='height' />

                                    </div>
                                ))
                            }
                          </div>
                           
                        </div>

                        {/* Full-size Height */}
                        <div className="space-y-3">
                            <div className="flex items-center">
                                <input
                                    type="radio"
                                    id="height-fullsize"
                                    name="height"
                                    value="Full-size"
                                    checked={selectedHeight === 'Full-size'}
                                    onChange={(e) => setSelectedHeight(e.target.value)}
                                    className="w-4 h-4 text-red-500 border-gray-300 focus:ring-red-500 focus:ring-2 accent-red-500"
                                />
                                <label htmlFor="height-fullsize" className="ml-2 text-lg font-medium text-gray-900">
                                    Full-size
                                </label>
                            </div>
                            <p className="text-gray-600 text-sm">
                                Full-size: &lt;6m length; &gt;1.83m height.
                            </p>
                            <div className='flex gap-3'>
                                {
                                    fullSizeHeight.map((item, index) => (
                                        <div key={index} className='flex items-center'>
                                            <Image className='w-12 h-12' src={item.image} height={20} width={20} alt='height' />
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    </div>
                </div>

                {/* Length Section */}
                <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Length</h3>

                    <div className="grid grid-cols-2 gap-6">
                        {/* Standard Length */}
                        <div className="space-y-3">
                            <div className="flex items-center">
                                <input
                                    type="radio"
                                    id="length-standard"
                                    name="length"
                                    value="Standard"
                                    checked={selectedLength === 'Standard'}
                                    onChange={(e) => setSelectedLength(e.target.value)}
                                    className="w-4 h-4 text-red-500 border-gray-300 focus:ring-red-500 focus:ring-2 accent-red-500"
                                />
                                <label htmlFor="length-standard" className="ml-2 text-lg font-medium text-gray-900">
                                    Standard
                                </label>
                            </div>
                            <p className="text-gray-600 text-sm">
                                Standard: &lt;6m length; &lt;1.83m height.
                            </p>
                            <div className='flex gap-3'>
                                {
                                    standardLength.map((item, index) => (
                                        <div key={index} className='flex items-center'>
                                            <Image className='w-12 h-12' src={item.image} height={20} width={20} alt='length' />
                                        </div>
                                    ))
                                }
                            </div>
                        </div>

                        {/* Full-size Length */}
                        <div className="space-y-3">
                            <div className="flex items-center">
                                <input
                                    type="radio"
                                    id="length-fullsize"
                                    name="length"
                                    value="Full-size"
                                    checked={selectedLength === 'Full-size'}
                                    onChange={(e) => setSelectedLength(e.target.value)}
                                    className="w-4 h-4 text-red-500 border-gray-300 focus:ring-red-500 focus:ring-2 accent-red-500"
                                />
                                <label htmlFor="length-fullsize" className="ml-2 text-lg font-medium text-gray-900">
                                    Full-size
                                </label>
                            </div>
                            <p className="text-gray-600 text-sm">
                                Full-size: &lt;6m length; &gt;1.83m height.
                            </p>
                            <div className='flex gap-3'>
                                {
                                    fullSizeLength.map((item, index) => (
                                        <div key={index} className='flex items-center'>
                                            <Image className='w-20 h-18' src={item.image} height={20} width={20} alt='length' />
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center">
                    <button
                        onClick={handleSubmit}
                        className="bg-gray-900 text-white px-16 py-3 rounded-md font-medium hover:bg-gray-800 transition-colors"
                    >
                        Submit
                    </button>
                </div>
            </div>
        </div>
    ) : null

    return typeof window !== 'undefined' && selectorContent
        ? createPortal(selectorContent, document.body)
        : null
}

export default VehicleSize